import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Badge from '@mui/material/Badge';
import Tooltip from '@mui/material/Tooltip';
import MenuIcon from '@mui/icons-material/esm/Menu';
import LogoutIcon from '@mui/icons-material/esm/Logout';
import ExpandLess from '@mui/icons-material/esm/ExpandLess';
import ExpandMore from '@mui/icons-material/esm/ExpandMore';
import NotificationsIcon from '@mui/icons-material/esm/Notifications';
import LockResetIcon from '@mui/icons-material/esm/LockReset';
import type { AppDispatch, RootState } from '../../store';
import { logout } from '../../store/slices/authSlice';
import { fetchProjects, setActiveProject } from '../../store/slices/projectSlice';
import { menuSections } from './MainLayout.menu';
import notificationService from '../../services/api/notificationService';
import { routePaths } from '../../routes/routePaths';
import fairmontLogo from '../../assets/branding/fairmont-estate-logo-mono-cream-1024.png';

const drawerWidth = 260;

const shellColors = {
  appbar: '#ffffff',
  drawer: '#f5f3f1',
  drawerBorder: '#e8e5e1',
  page: '#f6f5f3',
  text: '#1f1f1d',
  muted: '#6f6f6b',
  active: '#20201f',
  hover: 'rgba(32, 32, 31, 0.055)',
  glass: 'linear-gradient(135deg, rgba(255,255,255,0.98), rgba(255,255,255,0.72) 48%, rgba(233,235,232,0.34))',
};

interface MainLayoutProps {
  children: React.ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    overview: true,
    'field-operations': false,
    'money-supply': false,
    'project-controls': false,
    admin: false,
  });
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch() as AppDispatch;
  const { user } = useSelector((state: RootState) => state.auth);
  const { projects, activeProjectId } = useSelector((state: RootState) => state.projects);
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  useEffect(() => {
    if (!projects.length) return;
    const activeProjectIsVisible = projects.some((project) => project.id === activeProjectId);
    if (!activeProjectIsVisible) {
      dispatch(setActiveProject(projects[0].id));
    }
  }, [activeProjectId, dispatch, projects]);

  useEffect(() => {
    let mounted = true;
    if (!user?.id) {
      setUnreadNotifications(0);
      return undefined;
    }

    notificationService.getCenter({ readState: 'unread', limit: 1 })
      .then((result) => {
        if (mounted) setUnreadNotifications(result.totals.unread);
      })
      .catch(() => {
        if (mounted) setUnreadNotifications(0);
      });

    return () => {
      mounted = false;
    };
  }, [location.pathname, user?.id]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    await dispatch(logout());
    navigate('/login');
  };

  const visibleMenuSections = user?.role === 'qs'
    ? menuSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => (
          section.id === 'overview'
          || ['QS Workspace', 'Financials', 'Reports'].includes(item.text)
        )),
      }))
      .filter((section) => section.items.length > 0)
    : menuSections;

  const menuItems = visibleMenuSections.flatMap((section) => section.items);

  // Prefer exact match first so nested routes like /dashboard/pm are not
  // shadowed by their prefix /dashboard (which was matching first via startsWith
  // and incorrectly highlighting the parent Dashboard item).
  const activeMenuItem =
    menuItems.find((item) => location.pathname === item.path) ||
    menuItems.find((item) => location.pathname.startsWith(`${item.path}/`));

  const toggleSection = (sectionId: string) => {
    setOpenSections((current) => ({ ...current, [sectionId]: !current[sectionId] }));
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', px: 1.5, py: 1.75 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 54,
          height: 54,
          mb: 2,
          borderRadius: 1,
          overflow: 'visible',
          bgcolor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Box
          component="img"
          src={fairmontLogo}
          alt="Fairmont Estate"
          sx={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain' }}
        />
      </Box>

      <List sx={{ py: 0, flex: 1, overflowY: 'auto' }}>
        {visibleMenuSections.map((section) => {
          const sectionOpen = openSections[section.id] ?? true;
          return (
            <Box key={section.id} sx={{ mb: 1.25 }}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => toggleSection(section.id)}
                  dense
                  sx={{
                    px: 0.5,
                    py: 0.35,
                    borderRadius: 1,
                    color: shellColors.muted,
                    minHeight: 30,
                    '&:hover': {
                      bgcolor: 'transparent',
                      color: shellColors.text,
                    },
                  }}
                >
                  <ListItemText
                    primary={section.text}
                    primaryTypographyProps={{ fontSize: 11, fontWeight: 800, color: 'inherit' }}
                  />
                  <Box sx={{ color: 'inherit', display: 'flex', alignItems: 'center' }}>
                    {sectionOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                  </Box>
                </ListItemButton>
              </ListItem>
              <Collapse in={sectionOpen} timeout="auto" unmountOnExit>
                <List disablePadding>
                  {section.items.map((item) => {
                    const selected = Boolean(activeMenuItem && activeMenuItem.path === item.path);
                    return (
                      <ListItem key={item.text} disablePadding>
                        <ListItemButton
                          onClick={() => navigate(item.path)}
                          selected={selected}
                          sx={{
                            my: 0.2,
                            px: 0.85,
                            minHeight: 34,
                            borderRadius: 1,
                            color: selected ? shellColors.text : '#555550',
                            border: '1px solid transparent',
                            transition: 'background-color 180ms ease, background-image 180ms ease, border-color 180ms ease, box-shadow 180ms ease, transform 140ms ease',
                            '& .MuiListItemIcon-root': {
                              color: selected ? shellColors.text : '#7a7a75',
                            },
                            '& .MuiListItemText-primary': {
                              fontSize: 12,
                              fontWeight: selected ? 800 : 650,
                            },
                            '&.Mui-selected': {
                              color: shellColors.text,
                              borderColor: 'rgba(255,255,255,0.98)',
                              backgroundColor: '#ffffff',
                              backgroundImage: shellColors.glass,
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,1), 0 8px 18px rgba(35,35,33,0.06), 0 0 0 1px rgba(218,216,211,0.72)',
                            },
                            '&:hover, &.Mui-selected:hover': {
                              backgroundColor: '#ffffff',
                              backgroundImage: shellColors.glass,
                              borderColor: 'rgba(255,255,255,0.98)',
                              boxShadow: 'inset 0 1px 0 rgba(255,255,255,1), 0 10px 22px rgba(35,35,33,0.075), 0 0 0 1px rgba(218,216,211,0.86)',
                              transform: 'translateX(2px)',
                            },
                            '&:active': {
                              transform: 'translateX(2px) scale(0.99)',
                            },
                          }}
                        >
                          <ListItemIcon sx={{ minWidth: 30 }}>{item.icon}</ListItemIcon>
                          <ListItemText primary={item.text} />
                        </ListItemButton>
                      </ListItem>
                    );
                  })}
                </List>
              </Collapse>
            </Box>
          );
        })}
      </List>

      <Divider sx={{ my: 1.25, borderColor: shellColors.drawerBorder }} />
      <List sx={{ py: 0 }}>
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              px: 0.85,
              minHeight: 38,
              borderRadius: 1,
              color: '#555550',
              '&:hover': { bgcolor: '#ffffff' },
              '& .MuiListItemText-primary': { fontSize: 12, fontWeight: 650 },
            }}
          >
            <ListItemIcon sx={{ minWidth: 30, color: '#7a7a75' }}>
              <LogoutIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Logout" />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      {/* AppBar */}
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: shellColors.appbar,
          color: shellColors.text,
          boxShadow: 'none',
          borderBottom: `1px solid ${shellColors.drawerBorder}`,
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {activeMenuItem?.text || 'Dashboard'}
          </Typography>
          <Tooltip title="Notifications">
            <IconButton color="inherit" onClick={() => navigate(routePaths.notifications)} sx={{ mr: 1 }}>
              <Badge color="error" badgeContent={unreadNotifications} max={99}>
                <NotificationsIcon />
              </Badge>
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleProfileMenuOpen} sx={{ p: 0 }}>
            <Avatar sx={{ bgcolor: shellColors.active, color: 'common.white' }}>
              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </Avatar>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem disabled>
              <Typography variant="subtitle2">
                {user?.firstName} {user?.lastName}
              </Typography>
            </MenuItem>
            <MenuItem disabled>
              <Typography variant="caption" color="text.secondary">
                {user?.role}
              </Typography>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => { handleProfileMenuClose(); navigate(routePaths.changePassword); }}>
              <ListItemIcon>
                <LockResetIcon fontSize="small" />
              </ListItemIcon>
              Change password
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <LogoutIcon fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: shellColors.drawer,
              borderColor: shellColors.drawerBorder,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: shellColors.drawer,
              borderColor: shellColors.drawerBorder,
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
          bgcolor: shellColors.page,
        }}
      >
        <Toolbar />
        {children}
      </Box>
    </Box>
  );
}
